import { OngoingReportingType } from '../../interfaces/bbc'
import { QboCollectionLogStatus } from '../../interfaces/qbo'
import { QboTransactionSide, QboTransactionStatus } from '../../interfaces/qboReporting'
import { QBO_SYNCABLE_ACTIVITY_TYPES } from '../qbo'
import {
  QBO_AGING_BUCKETS,
  QBO_CREDIT_ENTITIES,
  QBO_TRANSACTION_ENTITIES,
  QBO_TRANSACTION_STATUS_LABELS,
  QBO_TRANSACTION_TYPE_LABELS,
} from '../qboReporting'
import { FilterFieldType, IFilter } from './types'

// Sortable columns for the QBO customer-mapping screen, driven through the
// standard useTable + TableFiltersRow path.
export const QBO_CUSTOMER_MAPPING_FILTERS: IFilter[] = [
  {
    field: 'entityName',
    type: FilterFieldType.Text,
    title: 'Customer (Dwight)',
    isSortable: true,
    align: 'left',
  },
  {
    field: 'qboCustomerName',
    type: FilterFieldType.Text,
    title: 'Customer (QuickBooks)',
    isSortable: true,
    align: 'left',
  },
  {
    field: 'qboAccountName',
    type: FilterFieldType.Text,
    title: 'Posts to',
    align: 'left',
  },
]

// Display label per log status. Shared so the status filter's options and the
// status chip in the log can't drift apart. (A Posted row still flagged
// is_pending renders as "Pending" instead — that depends on the row, not the
// status alone, so it lives with the chip.)
export const QBO_COLLECTION_LOG_STATUS_LABELS: Record<QboCollectionLogStatus, string> = {
  [QboCollectionLogStatus.Posted]: 'Posted',
  [QboCollectionLogStatus.Posting]: 'Posting…',
  [QboCollectionLogStatus.Failed]: 'Failed',
  [QboCollectionLogStatus.Blocked]: 'Closed period',
  [QboCollectionLogStatus.Dismissed]: 'Dismissed',
  [QboCollectionLogStatus.Skipped]: 'Skipped',
}

// Sort + filter columns for the QBO full sync log modal. `alias`/`dbField` are
// spelled out so Base.buildFiltersQuery emits real column references — the query
// is paginated, and the property-path form only holds while take/skip are set.
export const QBO_COLLECTION_LOG_FILTERS: IFilter[] = [
  {
    field: 'recordDate',
    dbField: 'record_date',
    alias: 'log',
    type: FilterFieldType.Date,
    title: 'Record date',
    isSortable: true,
    align: 'left',
  },
  {
    // The JE number posted as the entry's DocNumber ("Journal no." in QuickBooks).
    field: 'qboDocNumber',
    dbField: 'qbo_doc_number',
    alias: 'log',
    type: FilterFieldType.Text,
    title: 'JE #',
    isSortable: true,
    align: 'left',
  },
  {
    field: 'activityType',
    dbField: 'activity_type',
    alias: 'log',
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Type',
    isSortable: true,
    align: 'left',
    // Every kind of activity the sync can post, so a client can review or undo one
    // kind without disturbing the others. Sourced from the same allowlist the sync
    // itself filters on, so the log can never offer a type it cannot contain.
    options: QBO_SYNCABLE_ACTIVITY_TYPES.map((type) => ({ value: type, label: type })),
  },
  {
    field: 'amount',
    dbField: 'amount',
    alias: 'log',
    type: FilterFieldType.Amount,
    title: 'Amount',
    isSortable: true,
    align: 'right',
  },
  {
    field: 'status',
    dbField: 'status',
    alias: 'log',
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Status',
    isSortable: true,
    align: 'left',
    options: Object.values(QboCollectionLogStatus).map((status) => ({
      value: status,
      label: QBO_COLLECTION_LOG_STATUS_LABELS[status],
    })),
  },
]

// ---------------------------------------------------------------------------
// Invoices / Bills tabs (qbo_transaction, aliased "txn").
//
// Status, days past due and bucket are derived, so each is ONE SQL expression
// used for filtering, sorting and the value the row displays. A second
// definition in TypeScript would drift, and CURRENT_DATE keeps "today" on the
// database's clock.
// ---------------------------------------------------------------------------

const TXN = '"txn"'
const IS_CREDIT = `${TXN}."txn_type" IN (${QBO_CREDIT_ENTITIES.map((entity) => `'${entity}'`).join(', ')})`
const IS_OWED = `(${TXN}."balance" <> 0 AND NOT ${TXN}."is_voided")`

export const QBO_TRANSACTION_STATUS_SQL = `(CASE
  WHEN ${TXN}."is_voided" THEN '${QboTransactionStatus.Voided}'
  WHEN ${IS_CREDIT} THEN CASE WHEN ${TXN}."balance" = 0 THEN '${QboTransactionStatus.Applied}' ELSE '${QboTransactionStatus.Unapplied}' END
  WHEN ${TXN}."balance" = 0 THEN '${QboTransactionStatus.Paid}'
  WHEN ABS(${TXN}."balance") < ABS(${TXN}."total_amount") THEN '${QboTransactionStatus.PartiallyPaid}'
  ELSE '${QboTransactionStatus.Open}'
END)`

export const QBO_TRANSACTION_DAYS_PAST_DUE_SQL = `(CASE
  WHEN ${IS_OWED} AND NOT ${IS_CREDIT} AND ${TXN}."due_date" < CURRENT_DATE
  THEN CURRENT_DATE - ${TXN}."due_date"
  ELSE 0
END)`

export const QBO_TRANSACTION_BUCKET_SQL = `(CASE
  WHEN NOT ${IS_OWED} THEN NULL
  WHEN ${QBO_TRANSACTION_DAYS_PAST_DUE_SQL} = 0 THEN '${QBO_AGING_BUCKETS[0]}'
  WHEN ${QBO_TRANSACTION_DAYS_PAST_DUE_SQL} <= 30 THEN '${QBO_AGING_BUCKETS[1]}'
  WHEN ${QBO_TRANSACTION_DAYS_PAST_DUE_SQL} <= 60 THEN '${QBO_AGING_BUCKETS[2]}'
  WHEN ${QBO_TRANSACTION_DAYS_PAST_DUE_SQL} <= 90 THEN '${QBO_AGING_BUCKETS[3]}'
  ELSE '${QBO_AGING_BUCKETS[4]}'
END)`

// One config per side: the columns are the same, but the counterparty heading
// and the type options differ.
export const qboTransactionFilters = (side: QboTransactionSide): IFilter[] => [
  {
    field: 'counterpartyName',
    dbField: 'counterparty_name',
    alias: 'txn',
    type: FilterFieldType.Text,
    title: side === OngoingReportingType.AR ? 'Customer' : 'Vendor',
    isSortable: true,
    align: 'left',
  },
  {
    field: 'docNumber',
    dbField: 'doc_number',
    alias: 'txn',
    type: FilterFieldType.Text,
    title: 'Number',
    isSortable: true,
    align: 'left',
  },
  {
    field: 'txnType',
    dbField: 'txn_type',
    alias: 'txn',
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Type',
    isSortable: true,
    align: 'left',
    options: QBO_TRANSACTION_ENTITIES[side].map((entity) => ({
      value: entity,
      label: QBO_TRANSACTION_TYPE_LABELS[entity],
    })),
  },
  {
    field: 'status',
    dbField: QBO_TRANSACTION_STATUS_SQL,
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Status',
    isSortable: true,
    align: 'left',
    options: Object.values(QboTransactionStatus).map((status) => ({
      value: status,
      label: QBO_TRANSACTION_STATUS_LABELS[status],
    })),
  },
  {
    field: 'txnDate',
    dbField: 'txn_date',
    alias: 'txn',
    type: FilterFieldType.Date,
    title: 'Date',
    isSortable: true,
    // TableFiltersRow lays date headers out like numbers, whatever `align` says.
    align: 'right',
  },
  {
    field: 'dueDate',
    dbField: 'due_date',
    alias: 'txn',
    type: FilterFieldType.Date,
    title: 'Due date',
    isSortable: true,
    align: 'right',
  },
  {
    field: 'totalAmount',
    dbField: 'total_amount',
    alias: 'txn',
    type: FilterFieldType.Amount,
    title: 'Total',
    isSortable: true,
    align: 'right',
  },
  {
    field: 'balance',
    dbField: 'balance',
    alias: 'txn',
    type: FilterFieldType.Amount,
    title: 'Open balance',
    isSortable: true,
    align: 'right',
  },
  {
    field: 'agingBucket',
    dbField: QBO_TRANSACTION_BUCKET_SQL,
    type: FilterFieldType.List,
    isMultiple: true,
    title: 'Bucket',
    isSortable: true,
    align: 'left',
    options: QBO_AGING_BUCKETS.map((bucket) => ({ value: bucket, label: bucket })),
  },
  {
    field: 'daysPastDue',
    dbField: QBO_TRANSACTION_DAYS_PAST_DUE_SQL,
    type: FilterFieldType.Number,
    title: 'Days past due',
    isSortable: true,
    align: 'right',
  },
]
